import { Box, Stack, StackProps, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LocalizedString } from '@synergy/models';
import { makeStyles } from '@mui/styles';

import { Localized, LocalizedHtml } from '../localized';
import { NavigationLink } from '../sidebar-menu';
import { PoolTypography } from '../typography/pool-typography';

const useStyles = makeStyles(() => ({
    productDescription: {
        '& p': {
            margin: 0,
            padding: 0,

            // https://css-tricks.com/almanac/properties/l/line-clamp/
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
        },
    },
}));

interface ProductDetail {
    name: LocalizedString;
    productNumber?: string;
    getDescription: () => LocalizedString | undefined;
}

interface ProductDetailsProps extends StackProps {
    className?: string;
    product: ProductDetail;
    url?: string;
    /**
     * A position table leaves the description out: it runs over five lines and
     * makes the rows uneven, while the row has to be readable at a glance. It
     * stays in the position's own dialog and on the printed document (#2208).
     */
    showDescription?: boolean;
    /**
     * Puts the number behind the name instead of under it, which saves the
     * second line of every row. The text still wraps where it is too long, so
     * nothing is ever cut off (#2208).
     */
    inlineNumber?: boolean;
    /**
     * Cuts the name after this many lines with an ellipsis and gives it in
     * full on hover. A position table keeps its rows low with two (GF
     * 19.10.2026, R-179).
     */
    maxLines?: number;
}

export const ProductDetails = (props: ProductDetailsProps) => {
    const { className, product, url, showDescription = true, inlineNumber = false, maxLines, ...stackProps } = props;
    const classes = useStyles();

    const number = inlineNumber && product.productNumber && (
        <PoolTypography component="span" color="grey.500" typography="caption" sx={{ whiteSpace: 'nowrap' }}>
            {' '}
            {/* Short behind the name, where the long label would cost the line
                this is meant to save. */}
            <FormattedMessage
                id="app.common.productNumberInline"
                defaultMessage="(Nr. {value})"
                values={{ value: product.productNumber }}
            />
        </PoolTypography>
    );

    const nameContent = url ? (
        <>
            <NavigationLink productLink={true} target="_blank" url={url}>
                <Localized value={product.name} />
            </NavigationLink>
            {number}
        </>
    ) : (
        <>
            <Localized value={product.name} />
            {number}
        </>
    );

    return (
        <Stack className={className} gap={1} {...stackProps}>
            {maxLines ? (
                <Tooltip
                    title={
                        <>
                            <Localized value={product.name} />
                            {number}
                        </>
                    }
                    placement="bottom-start"
                    enterDelay={400}
                >
                    <Box
                        sx={{
                            typography: 'body2',
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitLineClamp: maxLines,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {nameContent}
                    </Box>
                </Tooltip>
            ) : (
                <PoolTypography typography="body2">{nameContent}</PoolTypography>
            )}

            {showDescription && product.getDescription?.() && (
                <PoolTypography color="grey.500" typography="caption">
                    <LocalizedHtml value={product.getDescription()} className={classes.productDescription} />
                </PoolTypography>
            )}

            {!inlineNumber && product.productNumber && (
                <PoolTypography color="grey.500" typography="caption">
                    <FormattedMessage id="app.common.productNumber" values={{ value: product.productNumber }} />
                </PoolTypography>
            )}
        </Stack>
    );
};
